/**
 * PushSoftAsk — contextual permission prompt.
 * 
 * Never triggers the native Notification.requestPermission() directly.
 * First shows a custom bottom sheet with value-aligned copy. Only when
 * the user taps "Turn on notifications" do we request the native prompt.
 * 
 * Dismissed state persists per-user-type for 14 days (no nagging).
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Bell, X, CheckCircle } from '@phosphor-icons/react';
import { isPushSupported, pushPermission, requestAndSubscribe } from '../lib/push';

const COPY_BY_CONTEXT = {
  supplier_lead: {
    title: 'Stay in the loop 🔔',
    body: 'Get notified when new enquiries arrive. Fast replies win more bookings ⚡',
    cta: 'Turn on notifications',
  },
  host_booking: {
    title: 'Stay in the loop 🔔',
    body: 'Get updates when suppliers respond and guests join your event.',
    cta: 'Turn on notifications',
  },
  guest_upload: {
    title: 'Stay in the loop 🔔',
    body: 'Get notified when new photos are added to this event 📸',
    cta: 'Turn on notifications',
  },
};

const DISMISS_DAYS = 14;
const dismissKey = (ctx) => `push_softask_dismissed_${ctx}`;

export function shouldShowSoftAsk(context) {
  if (!isPushSupported()) return false;
  const p = pushPermission();
  if (p === 'granted' || p === 'denied') return false;
  try {
    const v = localStorage.getItem(dismissKey(context));
    if (!v) return true;
    const ts = Number(v);
    if (Date.now() - ts > DISMISS_DAYS * 24 * 60 * 60 * 1000) return true;
    return false;
  } catch { return true; }
}

const PushSoftAsk = ({ context = 'supplier_lead', onClose }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const meta = COPY_BY_CONTEXT[context] || COPY_BY_CONTEXT.supplier_lead;

  const markDismissed = () => {
    try { localStorage.setItem(dismissKey(context), String(Date.now())); } catch {}
  };

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    const r = await requestAndSubscribe();
    setBusy(false);
    if (r.ok) {
      setDone(true);
      markDismissed();
      setTimeout(() => onClose?.(), 1500);
    } else if (r.reason === 'denied') {
      markDismissed();
      toast.info('You can turn notifications on later in your browser settings');
      onClose?.();
    } else {
      toast.error('Could not turn on notifications');
    }
  };

  const dismiss = () => {
    markDismissed();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6" onClick={dismiss} data-testid="push-soft-ask">
      <div className="w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-5 bg-[#15161B] border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end mb-2">
          <button onClick={dismiss} className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="push-soft-ask-close">
            <X size={12} weight="bold" />
          </button>
        </div>
        {done ? (
          <div className="text-center py-2" data-testid="push-soft-ask-success">
            <CheckCircle size={28} weight="fill" className="mx-auto mb-2 text-emerald-300" />
            <p className="font-black text-white text-sm mb-0.5">Notifications on 🔔</p>
            <p className="text-white/50 text-xs">You'll get updates instantly</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.2), rgba(194,157,82,0.1))' }}>
                <Bell size={22} weight="fill" className="text-[#C29D52]" />
              </div>
              <h3 className="text-lg font-black text-white mb-1" data-testid="push-soft-ask-title">{meta.title}</h3>
              <p className="text-white/55 text-xs leading-relaxed">{meta.body}</p>
            </div>
            <button
              onClick={accept}
              disabled={busy}
              className="w-full py-3 rounded-xl text-sm font-black text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="push-soft-ask-accept"
            >
              {busy ? 'Enabling…' : meta.cta}
            </button>
            <button
              onClick={dismiss}
              className="w-full py-2.5 mt-1 rounded-xl text-xs font-bold text-white/50 hover:text-white/80"
              data-testid="push-soft-ask-dismiss"
            >
              Not now
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PushSoftAsk;
